import { mdiClose, mdiFileDocument, mdiFilePdfBox, mdiFilePlusOutline, mdiImage, mdiMusicBox } from "@mdi/js";
import Icon from "@mdi/react";
import {
    TextField, MenuItem, FormControlLabel, Checkbox, Radio, RadioGroup, Grid,
    Typography,
    Button,
    Tooltip,
    Autocomplete
} from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useLoader } from "components/context/LoaderContext";
import { useSnackbar } from "components/context/SnackbarContext";
import useAxios from "hooks/useAxios";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DeleteDialog, DeleteDialogConfig, displayDate, PickerWrapper } from "utils/Utils";

const DynamicFields = ({ field, values, handleChange, handleBlur, errors, touched, setFieldValue, isEdittable = false, isSpecialField = false, selectedFiles = [] }) => {

    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();

    const fieldName = field.field_name;
    const fieldLabel = field.label || field.field_name;
    const isError = Boolean(errors?.[fieldName] && touched?.[fieldName]);
    const matchedField = selectedFiles.find(v => v.field_name === fieldName);
    const fieldValue = matchedField?.value || '';
    const fieldModelId = matchedField?.model_id || '';
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);
    const [fieldValueState, setFieldValueState] = useState([]);

    useEffect(() => {
        if (field.display_type === 'file' && typeof fieldValue === 'string') {
            setFieldValueState(fieldValue && fieldValue.split(','));
        }
        // if (field.display_type === 'selct' && field.is_multiple === 'yes') {
        //     setFieldValueState(fieldValue && fieldValue.split(','));
        // }
    }, [fieldValue]);

    // For select, radio, checkbox
    let options = [];
    if (['select', 'checkbox', 'radio'].includes(field.display_type)) {
        try {
            options = field.json_data_value; //JSON.parse(field.json_data_value || '[]');
        } catch (e) {
            console.error(`Invalid JSON for ${fieldName}`);
        }
    }

    const iconMapping = {
        image: mdiImage,
        audio: mdiMusicBox,
        file: mdiFileDocument,
        pdf: mdiFilePdfBox,
        // Add more mappings as needed
    };
    const [fileErrors, setFileErrors] = useState([]);
    const MAX_FILE_SIZE_MB = 4;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const handleDeleteFile = (fieldName, fileName, custom_field_id) => {
        const data = {
            model_id: fieldModelId ? parseInt(fieldModelId) : '',
            model_name: field?.model,
            custom_field_id: custom_field_id,
            field_name: fieldName,
            file_name: fileName,
        }
        try {
            DeleteDialog.fire({
                ...DeleteDialogConfig({
                    confirm: async () => {
                        showLoader()
                        await axiosInstance.delete(`api/custom-field-value/delete`, { data })
                            .then((response) => {
                                if (response.status === 200) {
                                    showSnackbar(response.data.message, "success");
                                    hideLoader();
                                    setFieldValueState(prev =>
                                        prev.filter(path => !path.includes(fileName))
                                    );
                                }
                            })
                            .then((json) => { })
                            .catch((error) => {
                                if (error.response) {
                                    hideLoader();
                                    if (error.response.status === 400) {
                                        showSnackbar(error.response.data.message, "warning");
                                    }
                                }
                            });
                    },
                    cancel: () => {
                        hideLoader();
                    }
                })
            });
        } catch (error) {
            hideLoader();
            showSnackbar('Oops Something want wrong..!', "error");
        }
    }

    switch (field.display_type) {
        case 'website':
        case 'text':
            return (
                <TextField
                    label={fieldLabel}
                    name={fieldName}
                    type={'text'}
                    variant="filled"
                    fullWidth
                    value={values[fieldName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={isError}
                    disabled={isEdittable}
                    helperText={isError && errors[fieldName]}
                />
            );
        case 'email':
            return (
                <TextField
                    label={fieldLabel}
                    name={fieldName}
                    type={'email'}
                    variant="filled"
                    fullWidth
                    value={values[fieldName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={isError}
                    disabled={isEdittable}
                    helperText={isError && errors[fieldName]}
                />
            );

        case 'number':
            return (
                <TextField
                    label={fieldLabel}
                    name={fieldName}
                    type={'number'}
                    variant="filled"
                    fullWidth
                    value={values[fieldName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={isError}
                    disabled={isEdittable}
                    helperText={isError && errors[fieldName]}
                    inputProps={{
                        inputMode: 'numeric', // for mobile keyboards
                        pattern: '[0-9]*'     // allows only digits
                    }}
                />
            );

        case 'date':
            return (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        label={fieldLabel}
                        format={displayDate(preferredDateFormat, 'half')}
                        views={['year', 'month', 'day']}
                        slotProps={{
                            textField: {
                                variant: 'filled',
                                name: fieldName || '',
                                type: 'text',
                                error: isError,
                                helperText: isError && errors[fieldName]
                            }
                        }}
                        disabled={isEdittable}
                        name={fieldName}
                        //value={values[fieldName] ? moment(values[fieldName]).tz(preTimeZoneKey) : null}
                        value={values && values[fieldName] ? moment(values[fieldName]).tz(preTimeZoneKey) : null}
                        // onChange={(value) => {
                        //     setFieldValue(fieldName, value ? value.format('YYYY-MM-DD') : '', true);
                        // }}
                        onChange={(value) => {
                            const formatted = value ? value.format('YYYY-MM-DD') : '';
                            handleChange({ target: { value: formatted } }); // simulate event //for quote item only
                            setFieldValue(fieldName, value ? value.format('YYYY-MM-DD') : '', true);
                        }}
                        onBlur={handleBlur}
                    />
                </LocalizationProvider>
            );

        case 'datetime':
            //let rowData = String(values[fieldName] || null);
            return (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                        label={fieldLabel}
                        format={preferredDateFormat}
                        views={["day", "hours", "minutes", "month", "year"]}
                        slots={{
                            popper: PickerWrapper
                        }}
                        disabled={isEdittable}
                        slotProps={{
                            textField: {
                                variant: 'filled',
                                name: fieldName || '',
                                type: 'text',
                                error: isError,
                                helperText: isError && errors[fieldName]
                            }
                        }}
                        name={fieldName}
                        value={values[fieldName] ? moment(values[fieldName]).tz(preTimeZoneKey) : null}
                        // onChange={(value) => 
                        //     setFieldValue(fieldName, value.format('YYYY-MM-DD HH:mm:ss'), true)
                        // }
                        onChange={(value) => {
                            const formatted = value ? value.format('YYYY-MM-DD HH:mm:ss') : '';
                            handleChange({ target: { value: formatted } }); // simulate event //for quote item only
                            setFieldValue(fieldName, value ? value.format('YYYY-MM-DD HH:mm:ss') : '', true);
                        }}
                        onBlur={handleBlur}
                    />
                </LocalizationProvider>
            );


        case 'textarea':
            return (
                <TextField
                    label={fieldLabel}
                    name={fieldName}
                    variant="filled"
                    fullWidth
                    multiline
                    minRows={3}
                    value={values[fieldName] || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={isError}
                    helperText={isError && errors[fieldName]}
                    disabled={isEdittable}
                />
            );

        case 'select':
            if (field.is_multiple === 'yes') {
                return (
                    <Autocomplete
                        multiple
                        options={options}
                        getOptionLabel={(option) => option.label}
                        // Here: value must be an array of strings, not objects
                        value={
                            options.filter((opt) =>
                                (values[fieldName] || []).includes(opt.value)
                            )
                        }
                        onChange={(event, newValue) => {
                            setFieldValue(
                                fieldName,
                                newValue.map((opt) => opt.value)
                            );
                        }}
                        onBlur={handleBlur}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={fieldLabel}
                                variant="filled"
                                error={isError}
                                helperText={isError && errors[fieldName]}
                                disabled={isEdittable}
                            />
                        )}
                    />
                );
            } else {
                return (
                    <TextField
                        select
                        label={fieldLabel}
                        name={fieldName}
                        variant="filled"
                        fullWidth
                        value={isSpecialField ? values : values[fieldName] || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={isError}
                        helperText={isError && errors[fieldName]}
                        disabled={isEdittable}
                    >
                        {options.map((opt, idx) => (
                            <MenuItem key={idx} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            }

        case 'checkbox':
            return (
                <div className="form-group mb-16">
                    {fieldLabel && <Typography
                        variant="body-large-bold"
                        component={'p'}
                        color={isError ? 'error' : 'text.primary'}
                        style={{ marginBottom: 4 }}
                    >
                        {fieldLabel}
                    </Typography>}
                    {options.map((opt, idx) => (
                        <FormControlLabel
                            key={idx}
                            disabled={isEdittable}
                            control={
                                <Checkbox
                                    checked={(values[fieldName] || []).includes(opt.value)}
                                    onChange={(e) => {
                                        const newValue = [...(values[fieldName] || [])];
                                        if (e.target.checked) {
                                            newValue.push(opt.value);
                                        } else {
                                            const index = newValue.indexOf(opt.value);
                                            if (index > -1) newValue.splice(index, 1);
                                        }
                                        handleChange({
                                            target: {
                                                name: fieldName,
                                                value: newValue,
                                            }
                                        });
                                    }}
                                    name={fieldName}
                                />
                            }
                            label={opt.label}
                        />
                    ))}
                    {isError && (
                        <Typography variant="caption" color="error" style={{ marginLeft: 14 }}>
                            {errors[fieldName]}
                        </Typography>
                    )}
                </div>
            );

        case 'radio':
            return (
                <div className="form-group mb-16">
                    {fieldLabel && <Typography
                        variant="body-large-bold"
                        component={'p'}
                        color={isError ? 'error' : 'text.primary'}
                        style={{ marginBottom: 4 }}
                    >
                        {fieldLabel}
                    </Typography>}
                    <RadioGroup
                        name={fieldName}
                        value={values[fieldName] || ''}
                        onChange={handleChange}
                        disabled={isEdittable}
                        row
                    >
                        {options.map((opt, idx) => (
                            <FormControlLabel key={idx} value={opt.value} control={<Radio />} label={opt.label} />
                        ))}
                    </RadioGroup>
                    {isError && (
                        <Typography variant="caption" color="error" style={{ marginLeft: 14 }}>
                            {errors[fieldName]}
                        </Typography>
                    )}
                </div>
            );

        case 'file':
            return (
                <>
                    <div className="attachment-center">
                        <div className="title">
                            <Typography variant="title-medium-bold" component={'p'} color={isError ? 'error' : 'text.primary'} style={{ marginBottom: 4 }}>{fieldLabel}</Typography> <Typography variant="title-small" className="file-size">(Max Size 4 MB)</Typography>
                        </div>

                        <div className="psuedo-upload-block">
                            <div className="content">
                                <Button color="secondary" className="btn-block" tabIndex={-1}>
                                    <span>
                                        <Icon path={mdiFilePlusOutline} color={'currentColor'} size={1} />
                                        <span>Upload File</span>
                                    </span>
                                </Button>
                            </div>
                            <input
                                type="file"
                                multiple={field.is_multiple === 'yes' ? true : false}
                                name={fieldName}
                                className="psuedo-upload-element"
                                disabled={isEdittable}
                                accept={field.accept || ".jpeg,.png,.jpg,.gif,.svg,.pdf,.mp4,.mp3,.3gp,.avi,.xls,.xlsx,.doc,.docx,.ods,.odt,.odp,.csv,.txt"}
                                onChange={(event) => {
                                    const selectedFiles = Array.from(event.currentTarget.files || []);
                                    const acceptedTypes = (field.accept || "").split(',').map(t => t.trim().toLowerCase());

                                    const validFiles = [];
                                    const newFileErrors = [];

                                    selectedFiles.forEach((file) => {
                                        const ext = file.name.split('.').pop().toLowerCase();
                                        const isAcceptedType = acceptedTypes.length === 0 || acceptedTypes.some(type => file.name.toLowerCase().endsWith(type));
                                        const isAcceptedSize = file.size <= MAX_FILE_SIZE_BYTES;

                                        if (isAcceptedType && isAcceptedSize) {
                                            validFiles.push(file);
                                        } else {
                                            const errors = [];
                                            if (!isAcceptedType) errors.push("Unsupported file type");
                                            if (!isAcceptedSize) errors.push(`File exceeds ${MAX_FILE_SIZE_MB}MB`);
                                            newFileErrors.push({ name: file.name, errors });
                                        }
                                    });

                                    setFileErrors(newFileErrors);
                                    setFieldValue(fieldName, validFiles);
                                    if (selectedFiles.length === 0) {
                                        setFileErrors([]);
                                    }
                                }}
                                onBlur={handleBlur}
                            />
                            {isError && (
                                <Typography variant="caption" color="error" style={{ marginLeft: 14 }}>
                                    {errors[fieldName]}
                                </Typography>
                            )}
                        </div>
                    </div>

                    {fileErrors.length > 0 && (
                        <ul className="error-text" style={{ color: 'red', fontSize: '0.8rem' }}>
                            {fileErrors.map((fileErr, idx) => (
                                <li key={idx}>
                                    {fileErr.name}:{fileErr.errors.join(', ')}
                                </li>
                            ))}
                        </ul>
                    )}

                    {Array.isArray(values[fieldName]) && values[fieldName].length > 0 && (
                        <Grid container component={'ul'} spacing={1.5} className="attachments">
                            {values[fieldName].map((attachment, index) => {
                                const fileExtension = attachment?.name?.split('.').pop().toLowerCase();
                                let fileType = '';
                                if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) fileType = 'image';
                                else if (['mp3', 'wav', 'ogg', 'mp4'].includes(fileExtension)) fileType = 'audio';
                                else if (['pdf'].includes(fileExtension)) fileType = 'pdf';
                                else fileType = 'file';

                                const IconComponent = iconMapping[fileType];

                                return (
                                    <Grid item component={'li'} xs={12} key={index + ''}>
                                        <a href="-" onClick={e => e.preventDefault()} className={(Array.isArray(errors[fieldName]) && errors[fieldName][index]) ? 'has-error' : ''}>
                                            <div className="icon-wrapper">
                                                {IconComponent && <Icon path={IconComponent} color={'currentColor'} size={1} />}
                                            </div>
                                            <div className="info">
                                                <Typography variant="body-large" component={'p'}>{attachment.name}</Typography>
                                                {attachment.size && <Typography variant="body-medium">Size: {(attachment?.size / 1024 / 1024).toFixed(2)} MB</Typography>}
                                            </div>
                                        </a>
                                        <Tooltip title="Remove">
                                            <button type="button" className="icon-button size-small" onClick={() => {
                                                const updated = [...values[fieldName]];
                                                updated.splice(index, 1);
                                                setFieldValue(fieldName, updated);
                                            }}>
                                                <Icon path={mdiClose} color={'currentColor'} size={1} />
                                            </button>
                                        </Tooltip>
                                        {
                                            Array.isArray(errors[fieldName]) && errors[fieldName][index] && (
                                                <ul className='error-text'>
                                                    {Array.isArray(errors[fieldName][index]) ?
                                                        errors[fieldName][index].map((err, idx) => (
                                                            <li key={idx}>{err}</li>
                                                        )) : <li>{errors[fieldName][index]}</li>}
                                                </ul>
                                            )
                                        }
                                    </Grid>
                                );
                            })}
                        </Grid >
                    )}

                    {fieldValueState?.length > 0 && (
                        <Grid container component={'ul'} spacing={1.5} className="attachments">
                            {fieldValueState.map((attachment, index) => {
                                const fileName = attachment?.split('/').pop();
                                const fileExtension = attachment?.name?.split('.').pop().toLowerCase();
                                let fileType = '';
                                if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) fileType = 'image';
                                else if (['mp3', 'wav', 'ogg', 'mp4'].includes(fileExtension)) fileType = 'audio';
                                else if (['pdf'].includes(fileExtension)) fileType = 'pdf';
                                else fileType = 'file';

                                const IconComponent = iconMapping[fileType];

                                return (
                                    <Grid item component={'li'} xs={12} key={index + ''}>
                                        <a href="-" onClick={e => e.preventDefault()}>
                                            <div className="icon-wrapper">
                                                {IconComponent && <Icon path={IconComponent} color={'currentColor'} size={1} />}
                                            </div>
                                            <div className="info">
                                                <Typography variant="body-large" component={'p'}>{fileName}</Typography>
                                            </div>
                                        </a>
                                        <Tooltip title="Remove">
                                            <button type="button" className="icon-button size-small" onClick={() => {
                                                handleDeleteFile(fieldName, fileName, field?.id);
                                                // const updated = [...fieldValueState];
                                                // updated.splice(index, 1);
                                                // setFieldValueState(updated);
                                            }}>
                                                <Icon path={mdiClose} color={'currentColor'} size={1} />
                                            </button>
                                        </Tooltip>
                                    </Grid>
                                );
                            })}
                        </Grid >
                    )}


                </>
            );


        default:
            return null;
    }
};

export default DynamicFields;